import React, { Fragment, useState } from 'react';
import { Link } from '@carbon/react';
import { Events } from '@carbon/react/icons';
import AliasClusterButton from './alias-cluster-button';
import AliasClusterGraph from './alias-cluster-graph';
import { useAliasCluster } from './use-alias-cluster';
import type { AliasClusterInfo, AliasName } from './use-alias-pill-states';
import styles from './alias-fields.module.scss';

// "Entity Classification" value (mock entity-reconciliation v3): the entity's
// role in its alias cluster — "Primary" (the cluster's canonical, or a
// standalone entity) or "Alias" — followed by the alias-group pill when the
// entity is aliased. The pill opens the cluster graph (matches the ⓘ affordance
// in the mock).
export const EntityClassification: React.FC<{
  info?: AliasClusterInfo;
  entityId: number;
}> = ({ info, entityId }) => {
  const roleLabel = info?.role === 'alias' ? 'Alias' : 'Primary';
  return (
    <span className={styles.classification}>
      {roleLabel}
      {info?.pillState && (
        <>
          <span className={styles.dot}>·</span>
          <AliasClusterButton state={info.pillState} entityId={entityId} />
        </>
      )}
    </span>
  );
};

const MAX_NAMES = 2;

// "Aliases" value: the other members of the entity's alias cluster, names first
// (linked to their entity pages), then a "+N more" affordance that opens the
// full cluster graph. Renders an em dash when the entity has no aliases.
export const AliasesList: React.FC<{
  aliases: AliasName[];
  entityId: number;
  linkNames?: boolean;
}> = ({ aliases, entityId, linkNames = false }) => {
  const [open, setOpen] = useState(false);
  const { members, edges, fetchCluster } = useAliasCluster(entityId);

  if (aliases.length === 0) return <>—</>;

  const shown = aliases.slice(0, MAX_NAMES);
  const remaining = aliases.length - shown.length;

  const openGraph = () => {
    void fetchCluster();
    setOpen(true);
  };

  return (
    <span className={styles.aliases}>
      {/* Alias-group icon (latest mock): a two-person glyph preceding the alias
          names that opens the cluster graph for the whole family. */}
      <button
        type="button"
        className={styles.groupIcon}
        aria-label="View alias group"
        title="View alias group"
        onClick={(e) => {
          e.stopPropagation();
          openGraph();
        }}
      >
        <Events size={16} />
      </button>
      {shown.map((alias, index) => (
        <Fragment key={alias.id}>
          {index > 0 && ', '}
          {linkNames ? (
            <Link
              href={`/entities/${alias.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {alias.name}
            </Link>
          ) : (
            <span className={styles.name}>{alias.name}</span>
          )}
        </Fragment>
      ))}
      {remaining > 0 && (
        <>
          {' and '}
          <button
            type="button"
            className={styles.more}
            onClick={(e) => {
              e.stopPropagation();
              openGraph();
            }}
          >
            {remaining} more
          </button>
        </>
      )}
      {/* Mounted only while open — every alias row otherwise carries a hidden
          Carbon Modal subtree. */}
      {open && (
        <AliasClusterGraph
          open
          onClose={() => setOpen(false)}
          selectedEntityId={String(entityId)}
          members={members}
          edges={edges}
        />
      )}
    </span>
  );
};
